import type { EntityIntakeDefaults } from './entityFormationDefaults';
import { ENTITY_INTAKE_SECTIONS, getVisibleFieldKeysForStep } from './entityFormationFields';
import { US_STATES } from '../usStates';
import {
  TIMELINE_OPTIONS,
  REVENUE_EXPECTATIONS_OPTIONS,
  GROWTH_OUTLOOK_OPTIONS,
  INDUSTRY_OPTIONS,
  STRATEGIC_INTENT_OPTIONS,
  NUMBER_OF_OWNERS_OPTIONS,
  MANAGEMENT_PREFERENCE_OPTIONS,
  YES_NO_NOT_SURE_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_LATER_OPTIONS,
  LIABILITY_RISK_OPTIONS,
  RISK_FACTOR_OPTIONS,
  INITIAL_FUNDING_OPTIONS,
  ADDITIONAL_SERVICES_OPTIONS,
} from './entityFormationOptions';

/**
 * Validation errors as a map of field keys to error messages
 */
export type ValidationErrors = Record<string, string>;

/**
 * Map of field keys to their allowed option values
 */
const FIELD_OPTIONS_MAP: Record<string, readonly string[]> = {
  timeline: TIMELINE_OPTIONS.map((opt) => opt.value),
  revenue_expectations: REVENUE_EXPECTATIONS_OPTIONS.map((opt) => opt.value),
  growth_outlook: GROWTH_OUTLOOK_OPTIONS.map((opt) => opt.value),
  industry: INDUSTRY_OPTIONS.map((opt) => opt.value),
  strategic_intent: STRATEGIC_INTENT_OPTIONS.map((opt) => opt.value),
  number_of_owners: NUMBER_OF_OWNERS_OPTIONS.map((opt) => opt.value),
  management_preference: MANAGEMENT_PREFERENCE_OPTIONS.map((opt) => opt.value),
  future_ownership_changes: YES_NO_NOT_SURE_OPTIONS.map((opt) => opt.value),
  operates_outside_formation_state: YES_NO_NOT_SURE_OPTIONS.map((opt) => opt.value),
  has_employees: YES_NO_OPTIONS.map((opt) => opt.value),
  uses_contractors: YES_NO_OPTIONS.map((opt) => opt.value),
  sells_online: YES_NO_OPTIONS.map((opt) => opt.value),
  serves_nationally: YES_NO_OPTIONS.map((opt) => opt.value),
  risk_factors: RISK_FACTOR_OPTIONS.map((opt) => opt.value),
  liability_risk: LIABILITY_RISK_OPTIONS.map((opt) => opt.value),
  has_business_bank_account: YES_NO_OPTIONS.map((opt) => opt.value),
  uses_bookkeeping_software: YES_NO_OPTIONS.map((opt) => opt.value),
  discussed_tax_planning: YES_NO_OPTIONS.map((opt) => opt.value),
  owns_domain: YES_NO_OPTIONS.map((opt) => opt.value),
  website_timeline: YES_NO_LATER_OPTIONS.map((opt) => opt.value),
  interested_in_brand_protection: YES_NO_NOT_SURE_OPTIONS.map((opt) => opt.value),
  initial_funding: INITIAL_FUNDING_OPTIONS.map((opt) => opt.value),
  plans_to_raise_capital: YES_NO_NOT_SURE_OPTIONS.map((opt) => opt.value),
  additional_services: ADDITIONAL_SERVICES_OPTIONS.map((opt) => opt.value),
  us_citizen: YES_NO_OPTIONS.map((opt) => opt.value),
};

const VALID_STATE_CODES = new Set<string>(US_STATES.map((state) => state.code));

/**
 * Helper function to get human-readable field label
 */
function getFieldLabel(fieldKey: string): string {
  const labels: Record<string, string> = {
    full_legal_name: 'Full Legal Name',
    phone_number: 'Phone Number',
    email_address: 'Email Address',
    street_address: 'Street Address 1',
    street_address_2: 'Street Address 2',
    city: 'City',
    state: 'State',
    zip_code: 'ZIP Code',
    us_citizen: 'US Citizen',
  };
  return labels[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Validates a single step of the form
 * @param step - The step number (1-11)
 * @param data - The form data object
 * @param visibleFieldKeys - Optional array of visible field keys for this step. If not provided, will be calculated.
 * @returns Object mapping field keys to error messages
 */
export function validateStep(
  step: number,
  data: EntityIntakeDefaults,
  visibleFieldKeys?: string[]
): ValidationErrors {
  const errors: ValidationErrors = {};
  const section = ENTITY_INTAKE_SECTIONS.find((s) => s.step === step);

  if (!section) {
    return { step: `Invalid step: ${step}` };
  }

  // Use provided visibleFieldKeys or calculate them
  const fieldKeys = visibleFieldKeys || getVisibleFieldKeysForStep(step, data);

  // Required fields validation
  const requiredFields: (keyof EntityIntakeDefaults)[] = [
    'full_legal_name',
    'phone_number',
    'email_address',
    'street_address',
    'city',
    'state',
    'zip_code',
  ];

  for (const fieldKey of requiredFields) {
    if (fieldKeys.includes(fieldKey)) {
      const value = data[fieldKey] as string;
      if (!value || value.trim() === '') {
        errors[fieldKey] = `${getFieldLabel(fieldKey)} is required`;
      }
    }
  }

  // Validate client_signature matches full_legal_name (case-insensitive, trimmed)
  if (fieldKeys.includes('client_signature')) {
    const signature = (data.client_signature as string) || '';
    const fullName = (data.full_legal_name as string) || '';
    if (signature.trim().toLowerCase() !== fullName.trim().toLowerCase()) {
      errors.client_signature = 'Client Signature must match the Full Legal Name';
    }
  }

  // us_citizen is required (YesNo | null, but must not be null)
  if (fieldKeys.includes('us_citizen')) {
    if (data.us_citizen === null || data.us_citizen === undefined) {
      errors.us_citizen = 'US Citizen status is required';
    }
  }

  // operating_states must contain at least 1 state code or "nationwide"
  if (fieldKeys.includes('operating_states')) {
    if (!Array.isArray(data.operating_states) || data.operating_states.length === 0) {
      errors.operating_states = 'At least one operating state must be selected';
    } else {
      // If "nationwide" is selected, it's valid (and should be the only selection, but we validate that in UI)
      const hasNationwide = data.operating_states.includes('nationwide');
      const stateCodes = data.operating_states.filter((code: string) => code !== 'nationwide');
      const invalidCodes = stateCodes.filter((code: string) => !VALID_STATE_CODES.has(code));
      if (invalidCodes.length > 0) {
        errors.operating_states = `Invalid state codes: ${invalidCodes.join(', ')}`;
      }
    }
  }

  // Acknowledgments must be true
  const acknowledgmentFields: (keyof EntityIntakeDefaults)[] = [
    'acknowledge_not_attorney_or_cpa',
    'acknowledge_entity_formation_scope',
    'certify_accuracy',
  ];

  for (const fieldKey of acknowledgmentFields) {
    if (fieldKeys.includes(fieldKey)) {
      const value = data[fieldKey] as boolean;
      if (value !== true) {
        errors[fieldKey] = 'This acknowledgment is required';
      }
    }
  }

  // Conditional validations
  // citizenship_status required if us_citizen === 'no'
  if (fieldKeys.includes('citizenship_status')) {
    if (data.us_citizen === 'no') {
      const value = data.citizenship_status;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.citizenship_status = 'Citizenship status is required when not a US citizen';
      }
    }
  }

  // domain_name required if owns_domain === 'yes'
  if (fieldKeys.includes('domain_name')) {
    if (data.owns_domain === 'yes') {
      const value = data.domain_name;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.domain_name = 'Domain name is required when you own a domain';
      }
    }
  }

  // bookkeeping_software_name required if uses_bookkeeping_software === 'yes'
  if (fieldKeys.includes('bookkeeping_software_name')) {
    if (data.uses_bookkeeping_software === 'yes') {
      const value = data.bookkeeping_software_name;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.bookkeeping_software_name = 'Bookkeeping software name is required';
      }
    }
  }

  // industry_other required if industry includes 'other'
  if (fieldKeys.includes('industry_other')) {
    if (Array.isArray(data.industry) && data.industry.includes('other')) {
      const value = data.industry_other;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.industry_other = 'Please specify the other industry';
      }
    }
  }

  // strategic_intent_other required if strategic_intent includes 'other'
  if (fieldKeys.includes('strategic_intent_other')) {
    if (Array.isArray(data.strategic_intent) && data.strategic_intent.includes('other')) {
      const value = data.strategic_intent_other;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.strategic_intent_other = 'Please specify the other strategic intent';
      }
    }
  }

  // initial_funding_other required if initial_funding includes 'other'
  if (fieldKeys.includes('initial_funding_other')) {
    if (Array.isArray(data.initial_funding) && data.initial_funding.includes('other')) {
      const value = data.initial_funding_other;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.initial_funding_other = 'Please specify the other initial funding source';
      }
    }
  }

  // Validate option values (ensure radio/select values are valid keys from option constants)
  for (const fieldKey of fieldKeys) {
    const allowedValues = FIELD_OPTIONS_MAP[fieldKey];
    if (allowedValues) {
      const value = data[fieldKey as keyof EntityIntakeDefaults];
      
      if (Array.isArray(value)) {
        // Multi-select: validate all values are in allowed options
        if (value.length > 0) {
          const invalidValues = (value as string[]).filter((val: string) => !allowedValues.includes(val));
          if (invalidValues.length > 0) {
            errors[fieldKey] = `Invalid selection(s): ${invalidValues.join(', ')}`;
          }
        }
      } else if (value !== null && value !== undefined && value !== '') {
        // Single-select: validate value is in allowed options
        if (!allowedValues.includes(value as string)) {
          errors[fieldKey] = 'Please select a valid option';
        }
      }
    }
  }

  // Validate state select fields
  if (fieldKeys.includes('state')) {
    if (data.state && !VALID_STATE_CODES.has(data.state as string)) {
      errors.state = 'Please select a valid state';
    }
  }

  if (fieldKeys.includes('state_of_residence')) {
    if (data.state_of_residence && !VALID_STATE_CODES.has(data.state_of_residence as string)) {
      errors.state_of_residence = 'Please select a valid state';
    }
  }

  // Validate client_signature is required and matches full_legal_name (case-insensitive, trimmed)
  if (fieldKeys.includes('client_signature')) {
    const signature = (data.client_signature as string) || '';
    if (!signature || signature.trim() === '') {
      errors.client_signature = `${getFieldLabel('client_signature')} is required`;
    } else {
      const fullName = (data.full_legal_name as string) || '';
      if (fullName.trim() && signature.trim().toLowerCase() !== fullName.trim().toLowerCase()) {
        errors.client_signature = 'Client Signature must match the Full Legal Name';
      }
    }
  }

  return errors;
}

/**
 * Validates all steps of the form
 * @param data - The form data object
 * @returns Object mapping field keys to error messages (combines errors from all steps)
 */
export function validateAll(data: EntityIntakeDefaults): ValidationErrors {
  const allErrors: ValidationErrors = {};

  for (const section of ENTITY_INTAKE_SECTIONS) {
    const stepErrors = validateStep(section.step, data);
    // Merge errors from this step into allErrors
    Object.assign(allErrors, stepErrors);
  }

  return allErrors;
}
